import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '../../actions';
import * as selectors from '../../selectors';
import { components as Core } from '../../../core';
import { css, spacing, StyleSheet } from '../../../../styles';
import { CompanyDetailsChildModal } from './components/CompanyDetailsChildModal';
import { CompanyDetailsHeader } from './components/CompanyDetailsHeader';
import { CompanyDetailsTabs } from './components/tabs/CompanyDetailsTabs';
import { useCompanyDetailsForm } from './hooks/useCompanyDetailsForm';
import { MODAL_WIDTH, TAB_FIELDS, TABS } from './utils/constants';

/**
 * Displays editable company details and the related management tools.
 *
 * @param {object} props
 * @param {string} props.companyId
 * @param {Function} props.onClose
 */
export const CompanyDetailsModal = ({ companyId, onClose }) => {
    const dispatch = useDispatch();
    const company = useSelector(state => selectors.getCompany(state, companyId));
    const isUpdatingCompany = useSelector(state => selectors.isUpdatingCompany(state, companyId));
    const isDeleting = useSelector(state => selectors.isDeletingCompany(state, companyId));
    const isRemovingSubscription = useSelector(state => selectors.isRemovingSubscription(state, companyId));
    const subscriptionPlans = useSelector(state => selectors.getSubscriptionPlans(state));
    const {
        formData,
        getFieldValue,
        getUpdatePayload,
        hasChanges,
        onChangeProperty,
        resetFields
    } = useCompanyDetailsForm(company);
    const [activeChildModal, setActiveChildModal] = useState(null);
    const hasRequestedSubscriptionPlans = useRef(false);
    const previousIsDeleting = useRef(isDeleting);

    useEffect(() => {
        if (!subscriptionPlans.length && !hasRequestedSubscriptionPlans.current) {
            hasRequestedSubscriptionPlans.current = true;
            dispatch(actions.getSubscriptionPlans());
        }
    }, [dispatch, subscriptionPlans.length]);

    useEffect(() => {
        if (previousIsDeleting.current && !isDeleting && !company) onClose();
        previousIsDeleting.current = isDeleting;
    }, [company, isDeleting, onClose]);

    const onSaveFields = useCallback(fieldNames => {
        dispatch(actions.updateCompany(getUpdatePayload(fieldNames)));
    }, [dispatch, getUpdatePayload]);

    const onRemoveSubscription = useCallback(() => {
        return dispatch(actions.removeSubsription(companyId)).then(response => {
            if (response && response.data) resetFields(TAB_FIELDS[TABS.subscription]);
        });
    }, [companyId, dispatch, resetFields]);

    const onCloseChildModal = useCallback(() => setActiveChildModal(null), []);
    const onCloseCompanyDetails = useCallback(() => {
        setActiveChildModal(null);
        onClose();
    }, [onClose]);

    return (
        <>
            <Core.Modal
                isVisible={true}
                onClose={onClose}
                title={company ? <CompanyDetailsHeader companyId={companyId} /> : null}
                modalWidth={MODAL_WIDTH}
                className={css(styles.modal)}>
                {company ? (
                    <CompanyDetailsTabs
                        company={company}
                        companyId={companyId}
                        formData={formData}
                        getFieldValue={getFieldValue}
                        hasChanges={hasChanges}
                        isDeleting={isDeleting}
                        isRemovingSubscription={isRemovingSubscription}
                        isUpdatingCompany={isUpdatingCompany}
                        onChangeProperty={onChangeProperty}
                        onClose={onClose}
                        onOpenChildModal={setActiveChildModal}
                        onRemoveSubscription={onRemoveSubscription}
                        onSaveFields={onSaveFields}
                        subscriptionPlans={subscriptionPlans}
                    />
                ) : null}
            </Core.Modal>
            <CompanyDetailsChildModal
                activeModal={activeChildModal}
                companyId={companyId}
                onCloseChildModal={onCloseChildModal}
                onCloseCompanyDetails={onCloseCompanyDetails}
            />
        </>
    );
};

export default CompanyDetailsModal;

const styles = StyleSheet.create({
    modal: {
        maxWidth: 'calc(100vw - 32px)',
        '& .ant-modal-header': {
            padding: spacing.s3
        },
        '& .ant-modal-title': {
            margin: '0 !important'
        },
        '& .ant-modal-body': {
            // maxHeight: 'calc(100vh - 230px)',
            overflowY: 'auto',
            paddingTop: spacing.s4,
            paddingBottom: spacing.s4
        },
        '@media (max-width: 540px)': {
            '& .ant-modal-header': {
                padding: spacing.s2
            },
            '& .ant-modal-body': {
                maxHeight: 'calc(100vh - 205px)',
                paddingLeft: spacing.s3,
                paddingRight: spacing.s3
            }
        }
    }
});
